import {
  BrandsStep,
  ReportTypeStep
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/types';
import { DynamicSelectionsStep } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/utils/createDynamicSchema';

/**
 * Module-scoped cache for the three Presentations builder forms.
 *
 * `PresentationFormProvider` is sometimes unmounted and remounted *during* a
 * single user session — when `HomeAuthLayout` detects a Redux/URL time-period
 * mismatch it sets `isInitializing = true`, rendering `<Loading />` and
 * unmounting the entire content subtree. This destroys the `useForm` instances
 * and all user input.
 *
 * The cache lets the next mount re-hydrate `useForm` from the user's
 * last-known input so the user stays on their current step.
 *
 * To distinguish a transient remount (< 1 s, HomeAuthLayout loading cycle)
 * from an intentional navigation away (user clicks Profile, Downloads, etc.),
 * the provider calls `scheduleCacheClear()` on unmount. If it remounts within
 * `TRANSIENT_REMOUNT_WINDOW_MS` the timer is cancelled and the cache survives.
 * If it doesn't remount (user left), the timer fires and wipes the cache so
 * the next visit starts fresh.
 */
export type CachedPresentationFormState = {
  type?: ReportTypeStep;
  selections?: DynamicSelectionsStep;
  brands?: BrandsStep;
};

let cachedFormState: CachedPresentationFormState = {};

const TRANSIENT_REMOUNT_WINDOW_MS = 2000;
let clearTimer: ReturnType<typeof setTimeout> | null = null;

export const getCachedPresentationFormState = (): CachedPresentationFormState => cachedFormState;

export const setCachedPresentationFormType = (values: ReportTypeStep): void => {
  cachedFormState.type = values;
};

export const setCachedPresentationFormSelections = (values: DynamicSelectionsStep): void => {
  cachedFormState.selections = values;
};

export const setCachedPresentationFormBrands = (values: BrandsStep): void => {
  cachedFormState.brands = values;
};

export const clearPresentationFormCache = (): void => {
  if (clearTimer) {
    clearTimeout(clearTimer);
    clearTimer = null;
  }
  cachedFormState = {};
};

/**
 * Schedule a cache wipe after `TRANSIENT_REMOUNT_WINDOW_MS`.
 * Called from `PresentationFormProvider`'s cleanup.
 */
export const scheduleCacheClear = (): void => {
  if (clearTimer) {
    clearTimeout(clearTimer);
  }
  clearTimer = setTimeout(() => {
    cachedFormState = {};
    clearTimer = null;
  }, TRANSIENT_REMOUNT_WINDOW_MS);
};

/**
 * Cancel any pending cache wipe.
 * Called at the top of `PresentationFormProvider`'s mount effect — if we
 * remounted within the window this was a transient remount and the cache
 * should survive.
 */
export const cancelScheduledCacheClear = (): void => {
  if (clearTimer) {
    clearTimeout(clearTimer);
    clearTimer = null;
  }
};
